import React, { useEffect, useRef, useState } from "react";
import "./voiceInterviews.scss";
import { Table } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import {
  getCategorySummary,
  getEngagements,
  getRecentCalls,
} from "../../Redux/ActionCreators/interviewResultsAction";
import {
  getDashboardChartData,
  getJobList,
} from "../../Redux/ActionCreators/dashboardAction";
import { useDispatch, useSelector } from "react-redux";
import ReactPlayer from "react-player";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import Select from "react-select";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import InterviewProgressCircle from "../../utils/InterviewProgressCircle";

const VideoInterviewsReport = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isInitialLoad = useRef(true);

  const { resultSummary, recentCalls, engagement } = useSelector(
    (state) => state.interviewResults
  );
  const { jobList, chartData } = useSelector((state) => state.dashboard);

  const isMobile = window.innerWidth < 767;

  const [dateRange, setDateRange] = useState([
    moment().subtract(6, "days").toDate(),
    new Date(),
  ]);
  const [startDate, endDate] = dateRange;
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [garphLoading, setGraphLoading] = useState(false);
  const [callsLoading, setCallsLoading] = useState(false);
  const [engagementLoading, setEngagementLoading] = useState(false);

  const [showPopup, setShowPopup] = useState(false);
  const [popupItem, setPopupItem] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [openAccordion, setOpenAccordion] = useState(null);

  const jobOptions = jobList.map((job) => ({
    value: job.id,
    label: job.title,
  }));

  const customStyles = {
    control: (base) => ({ ...base, width: "175px" }),
    menu: (base) => ({ ...base, width: "230px" }),
  };

  const toggleAccordion = (key) => {
    setOpenAccordion((prev) => (prev === key ? null : key));
  };

  const fetchCandidateSummary = async () => {
    setSummaryLoading(true);
    const payload = {
      start_date: moment(startDate).format("YYYY-MM-DD"),
      end_date: moment(endDate).format("YYYY-MM-DD"),
      event_category: "73397da6-063f-4937-b364-e77455c2e51b",
      ...(selectedJobId && { job_id: selectedJobId }),
    };
    try {
      await dispatch(getCategorySummary(payload));
    } catch (error) {
      console.error("Error fetching candidate summary:", error);
    } finally {
      setSummaryLoading(false);
    }
  };

  const fetchRecentCalls = async () => {
    setCallsLoading(true);
    const payload = {
      event_category: "73397da6-063f-4937-b364-e77455c2e51b",
      limit: 6,
    };
    try {
      await dispatch(getRecentCalls(payload));
    } catch (error) {
      console.error("Error fetching recent calls:", error);
    } finally {
      setCallsLoading(false);
    }
  };

  const fetchChartData = async () => {
    setGraphLoading(true);
    const payload = {
      start_date: moment(startDate).format("YYYY-MM-DD"),
      end_date: moment(endDate).format("YYYY-MM-DD"),
      event_category: "73397da6-063f-4937-b364-e77455c2e51b",
      ...(selectedJobId && { job_id: selectedJobId }),
    };
    try {
      await dispatch(getDashboardChartData(payload));
    } catch (err) {
      console.error("Error fetching chart data:", err);
    } finally {
      setGraphLoading(false);
    }
  };

  const fetchEngagements = async (page = 1, search = "") => {
    setEngagementLoading(true);
    const payload = {
      event_category_id: "73397da6-063f-4937-b364-e77455c2e51b",
      page: page,
      page_size: 5,
      ...(search.length >= 3 ? { search } : {}),
    };
    try {
      await dispatch(getEngagements(payload));
    } catch (error) {
      console.error("Error fetching engagements:", error);
    } finally {
      setEngagementLoading(false);
    }
  };

  const fetchInitialData = async () => {
    setSummaryLoading(true);
    setGraphLoading(true);
    setCallsLoading(true);
    setEngagementLoading(true);
    await fetchCandidateSummary();
    await fetchChartData();
    await fetchRecentCalls();
    await fetchEngagements();
  };

  useEffect(() => {
    dispatch(getJobList());
  }, []);

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (!startDate || !endDate) return;
    if (isInitialLoad.current) {
      isInitialLoad.current = false;
      return;
    }
    fetchCandidateSummary();
    fetchChartData();
    fetchRecentCalls();
  }, [startDate, endDate, selectedJobId]);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);

    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    }).format(date);
  };

  const allowedLabels = [
    "Video Interview - Completed",
    "Video Interview - Assigned",
  ];

  const hasAnyData = (chartData?.series?.length || 0) > 0;
  const filteredSeries = hasAnyData
    ? chartData.series.filter(s => allowedLabels.includes(s.label))
    : [];
  const hasFiltered = filteredSeries.length > 0;

  const placeholderSeries = allowedLabels.map(label => ({
    name: label.replace("Video Interview - ", ""),
    data: (chartData?.labels || []).map(() => null),
    color:
    label.includes("Assigned") ? "#0048B7" :
    label.includes("Completed") ? "#28A745" : undefined,
    marker: { enabled: false }
  }));

  const chartOptions = {
    chart: { type: "line", height: 300 },
    title: { text: null },
    xAxis: {
      categories: chartData?.labels || [],
      tickmarkPlacement: "on",
      gridLineWidth: 1,
    },
    yAxis: {
      title: { text: null },
      gridLineWidth: 1,
      min: (!hasAnyData || !hasFiltered) ? 0 : null,
      max: (!hasAnyData || !hasFiltered) ? 100 : null,
      tickInterval: (!hasAnyData || !hasFiltered) ? 20 : null,
      allowDecimals: false,
    },
    legend: {
      enabled: true,
      layout: "horizontal",
      align: "left",
      verticalAlign: "top",
      itemDistance: 30,
      itemStyle: {
        fontFamily: '"inter", sans-serif',
        fontSize: "12px",
        color: "#000000",
        whiteSpace: "nowrap",
      },
    },
    tooltip: { shared: true, crosshairs: true },
    series: (hasFiltered
      ? filteredSeries.map(s => ({
          name: s.label.replace("Video Interview - ", ""),
          data: s.data,
          color: 
          s.label.includes("Assigned") ? "#0048B7":
          s.label.includes("Completed") ? "#28A745" : undefined,
          marker: { enabled: false },
          connectNulls: true
        }))
      : placeholderSeries),
    credits: { enabled: false },
  };

  const openVideoPopup = (item) => {
    setPopupItem(item);
    setShowPopup(true);
    setPlaying(true);
  };

  const closeVideoPopup = () => {
    setPlaying(false);
    setShowPopup(false);
    setTimeout(() => setPopupItem(null), 0);
  };

  return (
    <div className="voice-interview">
      <div className="voice-interview-header">
        <h3>Video interviews</h3>
      </div>
      <div className="voice-interview-data">
        <div className="voice-interview-graph">
          <div className="voice-interview-head">
            <h4>Performance summary</h4>
            <div className="d-flex" style={{ gap: "12px" }}>
              <Select
                options={jobOptions}
                onChange={(opt) => setSelectedJobId(opt ? opt.value : null)}
                isClearable
                isSearchable
                placeholder="Select Job"
                className="job-select-dropdown"
                styles={customStyles}
              />
              <DatePicker
                selectsRange
                startDate={startDate}
                endDate={endDate}
                onChange={(update) => {
                  setDateRange(update);
                }}
                isClearable={true}
                dateFormat="dd MMM"
                className="form-control date-range-picker"
                placeholderText="Select date range"
              />
            </div>
          </div>
          <div className="voice-interview-chart">
            {summaryLoading  ? (
              <div className="loading-overlay" style={{ height: "226px" }}>
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden"></span>
                  </div>
                </div>
            ) : (
              <div className="voice-interview-bar">
                <InterviewProgressCircle
                  assigned={resultSummary?.total_interviews || 0}
                  scheduled={resultSummary?.queued_interviews || 0}
                  completed={resultSummary?.completed_interviews || 0}
                  size={220}
                  strokeWidth={10}
                />
                <div
                  className="d-flex justify-content-center"
                  style={{ gap: "25px", width: "250px" }}
                >
                  <div></div>
                  <div className="summary-count">
                    <h2 style={{ color: "#4CC948" }}>
                      <img src="/images/shield-trust-green.svg" alt="" />
                      &nbsp;{resultSummary?.completed_interviews || 0}
                    </h2>
                    <p>Completed</p>
                  </div>
                  <div></div>
                  <div></div>
                </div>
              </div>
            )}
            <div className="voice-interview-graph-data">
              {garphLoading ? (
                <div className="loading-overlay" style={{ height: "226px" }}>
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden"></span>
                  </div>
                </div>
              ) : (
                <div style={{ position: "relative" }}>
                  <HighchartsReact
                    className="highcharts-container"
                    highcharts={Highcharts}
                    options={chartOptions}
                    key={JSON.stringify({
                      l: chartData?.labels,
                      s: filteredSeries,
                    })}
                  />
                  {(!hasAnyData || !hasFiltered) && (
                    <div className="no-data-overlay">
                      <h6>
                        NO DATA <br />
                        AVAILABLE
                      </h6>
                      <span style={{ top: "75%" }}>
                        Data unavailable ! Please wait for systems to get
                        updated with activity data
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="voice-interview-recent-calls">
        <div className="recent-calls-head">
          <h4>Recent Video Interviews</h4>
        </div>
        <div className="recent-calls">
          {callsLoading ? (
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ width: "100%" }}
            >
              <div className="loading-overlay" style={{ height: "225px" }}>
                <div className="spinner-border" role="status">
                  <span className="visually-hidden"></span>
                </div>
              </div>
            </div>
          ) : recentCalls.recent_calls?.length > 0 ? (
            recentCalls.recent_calls?.map((item, index) => {
              const initials = item?.meta_data?.data?.candidate?.name
                ? item?.meta_data?.data?.candidate?.name
                    .split(" ")
                    .map((word) => word[0])
                    .join("")
                    .slice(0, 2)
                    .toUpperCase()
                : "NA";
              return (
                <div
                  className="recent-calls-items"
                  key={index}
                  style={{
                    background: "#111927",
                    boxShadow: "0px 4px 10px 0px #0000004D",
                    height: "264px",
                    paddingTop: "12px",
                  }}
                >
                  <div
                    className="recent-calls-items-top"
                    style={{
                      background: "unset",
                      minHeight: "unset",
                      maxHeight: "unset",
                    }}
                  >
                    <div
                      className="recent-calls-initials"
                      style={{
                        background: "unset",
                        width: "unset",
                        height: "unset",
                        fontSize: "40px",
                        marginBottom: "5px",
                      }}
                    >
                      {initials}
                    </div>
                    <div
                      className="recent-calls-info"
                      style={{ marginBottom: "10px" }}
                    >
                      <h5 style={{ color: "#FFFFFF", marginBottom: "5px" }}>
                        {item?.meta_data?.data?.candidate?.name || "N/A"}
                      </h5>
                      <p style={{ color: "#8A9199", marginBottom: "5px" }}>
                        {item?.meta_data?.data?.job?.title || "No title"}
                      </p>
                    </div>
                  </div>
                  {item?.meta_data?.data?.stage?.result?.url && (
                    <div className="recent-calls-audio">
                      <button
                        className="play-pause-btn"
                        onClick={() => openVideoPopup(item)}
                        aria-label="Play video interview"
                        style={{ display: "flex", gap: "10px", left: "18%" }}
                      >
                        <hr
                          style={{
                            border: "1px solid #555C5E",
                            width: "36px",
                            height: "1px",
                            background: "#555C5E",
                          }}
                        />
                        <img src="/images/pink-play-circle.svg" alt="" />
                        <hr
                          style={{
                            border: "1px solid #555C5E",
                            width: "36px",
                            height: "1px",
                            background: "#555C5E",
                          }}
                        />
                      </button>
                    </div>
                  )}
                  <div className="recent-calls-items-bottom">
                    <div className="recent-calls-score">
                      <h6>Score</h6>
                      <h4>{item.meta_data?.data?.stage?.score.toFixed(2) || "N/A"}%</h4>
                    </div>
                    <p className="recent-calls-date">
                      {formatDate(item.call_date)}
                    </p>
                    <div className="d-flex justify-content-center">
                      <button
                        onClick={() =>
                          navigate(
                            `/jobs/${item.job_id}/candidates/${item.job_candidate_id}`
                          )
                        }
                      >
                        View details
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
              <div className="no-data-feed-wrapper" style={{background:'unset', width:'100%'}}>
                <div className="no-data-overlay">
                  <h6>NO DATA <br />AVAILABLE</h6>
                  <span style={{top:'75%'}}>Data unavailable ! Please wait for systems to get updated with activity data</span>
                </div>
              </div>
          )}
        </div>
      </div>
      {!isMobile ? 
      <div className="voice-jobs-data">
        <div className="voice-jobs-data-head">
          <h3>Top 5 job engagements</h3>
          <Table
            responsive
            className="job-table"
            style={{ borderRadius: "8px" }}
          >
            <thead>
              <tr>
                <th style={{ borderTopLeftRadius: "8px" }}>Job title</th>
                <th>
                  <div className="d-flex justify-content-center align-items-center">
                    <img src="/images/calendar-check.svg" alt="" />
                    &nbsp;&nbsp;<span>Scheduled</span>
                  </div>
                </th>
                <th>
                  <div className="d-flex justify-content-center align-items-center">
                    <img src="/images/check-circle.svg" alt="" />
                    &nbsp;&nbsp;<span>Completed</span>
                  </div>
                </th>
                <th>
                  <div className="d-flex justify-content-center align-items-center">
                    <img src="/images/angles-up-down.svg" alt="" />
                    &nbsp;&nbsp;<span>Avg Score</span>
                  </div>
                </th>
                <th>
                  <div className="d-flex justify-content-center align-items-center">
                    <img src="/images/arrow-circle-up.svg" alt="" />
                    &nbsp;&nbsp;<span>Top Score</span>
                  </div>
                </th>
                <th>
                  <div className="d-flex justify-content-center align-items-center">
                    <img
                      src="/images/arrow-circle-up.svg"
                      alt=""
                      style={{ rotate: "180deg" }}
                    />
                    &nbsp;&nbsp;<span>Low Score</span>
                  </div>
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {engagementLoading ? (
                <tr>
                  <td colSpan="6">
                    <div
                      className="loading-overlay"
                      style={{ height: "294px" }}
                    >
                      <div className="spinner-border" role="status">
                        <span className="visually-hidden"></span>
                      </div>
                    </div>
                  </td>
                </tr>
              ) : engagement?.data?.length > 0 ? (
                engagement?.data?.map((job) => (
                  <tr key={job.job_id}>
                    <td className="job-title">
                      {job.job_title ? job.job_title : "N/A"}
                    </td>
                    <td>
                      <div className="d-flex justify-content-center">
                        <p
                          className="engagement-number scheduled"
                          style={{
                            background: "#00DDFF0D",
                            border: "1px solid #00DDFF",
                          }}
                        >
                          {job.scheduled ? job.scheduled : 0}
                        </p>
                      </div>
                    </td>
                    <td>
                      <div className="d-flex justify-content-center">
                        <p
                          className="engagement-number completed"
                          style={{
                            background: "#4CC9481A",
                            border: "1px solid #4CC948",
                          }}
                        >
                          {job.completed ? job.completed : 0}
                        </p>
                      </div>
                    </td>
                    <td>
                      <div className="d-flex justify-content-center">
                        <p
                          className="engagement-number expired"
                          style={{
                            background: "#FFC8571A",
                            border: "1px solid #FFC857",
                          }}
                        >
                          {job.expired ? job.avg_score : 0}
                        </p>
                      </div>
                    </td>
                    <td>
                      <div className="d-flex justify-content-center">
                        <p
                          className="engagement-number expired"
                          style={{
                            background: "#4CC9481A",
                            border: "1px solid #4CC948",
                          }}
                        >
                          {job.expired ? job.top_score : 0}
                        </p>
                      </div>
                    </td>
                    <td>
                      <div className="d-flex justify-content-center">
                        <p
                          className="engagement-number expired"
                          style={{
                            background: "#FF1C581A",
                            border: "1px solid #FF1C58",
                          }}
                        >
                          {job.expired ? job.low_score : 0}
                        </p>
                      </div>
                    </td>
                    <td className="job-actions">
                      <button
                        className="view-job-btn"
                        onClick={() =>
                          navigate(`/jobs/${job.job_id}/candidates`)
                        }
                      >
                        View details
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr className="no-data-engagement-wrapper" style={{background:'unset', width:'100%', height:'200px'}}>
                  <td className="no-data-overlay" colSpan="10">
                    <h6>NO DATA <br />AVAILABLE</h6>
                    <span style={{top:'85%'}}>Data unavailable ! Please wait for systems to get updated with activity data</span>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
        {/* ===================== Video Popup ===================== */}
        {showPopup && popupItem && (
          <div className="video-popup-backdrop" onClick={closeVideoPopup}>
            <div className="video-popup" onClick={(e) => e.stopPropagation()}>
              <div className="video-popup-header">
                <div>
                  <div className="video-popup-name">
                    {popupItem?.meta_data?.data?.candidate?.name || "—"}
                  </div>
                  <div className="video-popup-title">
                    {popupItem?.meta_data?.data?.job?.title || "—"}
                  </div>
                </div>
                <button
                  className="close-btn"
                  onClick={closeVideoPopup}
                  aria-label="Close"
                >
                  <img src="/images/black-close.svg" alt="" />
                </button>
              </div>

              {/* Player */}
              <div className="react-player">
                <ReactPlayer
                  className="video-player"
                  url={popupItem?.meta_data?.data?.stage?.result?.url}
                  width="512px"
                  height="286px"
                  playing={playing}
                  controls
                  onPause={() => setPlaying(false)}
                  onPlay={() => setPlaying(true)}
                />
              </div>
            </div>
          </div>
        )}
      </div>
      :
      <div className="voice-jobs-data mobile-accordion-wrapper">
        <div className="accordion-item">
          <div className="accordion-header" onClick={() => toggleAccordion("engagement")}>
            <h4>Top 5 jobs engagement</h4>
            <img src="/images/chevron-down.svg" alt=""
              className={`accordion-arrow ${openAccordion === "engagement" ? "open" : ""}`}
            />
          </div>
          {openAccordion === "engagement" && (
              <div className="accordion-body">
                <div className="voice-engagement">
                  <div className="voice-engagement-table-top">
                    <div className="voice-engagement-added d-flex" style={{gap:'22px'}}>
                      <div className="voice-engagement-added-items">
                        <span style={{background: '#0048B71A', border:'1px solid #0048B7'}}></span>
                        <p>Assigned</p>
                      </div>
                      <div className="voice-engagement-added-items">
                        <span style={{background: '#00DDFF1A', border:'1px solid #00DDFF'}}></span>
                        <p>Scheduled</p>
                      </div>
                      <div className="voice-engagement-added-items">
                        <span style={{background: '#4CC9481A', border:'1px solid #4CC948'}}></span>
                        <p>Completed</p>
                      </div>
                    </div>
                    <div className="voice-engagement-table">
                    {engagementLoading ? (
                      <div className="loading-overlay" style={{ height: "294px" }}>
                        <div className="spinner-border" role="status">
                          <span className="visually-hidden"></span>
                        </div>
                      </div>
                        ) : engagement?.data?.length > 0 ? (
                          engagement?.data?.map((job) => (
                            <div className="voice-engagement-table-items" key={job?.job_id}>
                              <p>{job?.job_title ? job?.job_title : "N/A"}
                                <div className="d-flex" style={{gap:'8px', marginTop:'8px'}}>
                                  <span style={{background:'#0048B71A', border:'1px solid #0048B7'}}><img src="/images/person-plus-black.svg" alt="" width={12} height={12} />{job?.assigned ? job?.assigned : 0}</span>
                                  <span style={{background:'#00DDFF1A', border:'1px solid #00DDFF'}}><img src="/images/calendar-plus-icon.svg" alt="" width={12} height={12} />{job?.scheduled ? job?.scheduled : 0}</span>
                                  <span style={{background:'#4CC9481A', border:'1px solid #4CC948'}}><img src="/images/check-double-icon.svg" alt="" width={12} height={12} />{job?.completed ? job?.completed : 0}</span>
                                </div>
                              </p>
                              <p style={{ color: "#FF1C91", cursor: "pointer" }} onClick={() => navigate(`/jobs/${job?.job_id}/candidates`)}>
                                <img src="/images/chevron-down.svg" alt="" style={{rotate:'-90deg'}} width={16} height={16} />
                              </p>
                            </div>
                          ))
                        ) : (
                          <div className="no-data-engagement-wrapper" style={{background:'unset', width:'100%', height:'200px'}}>
                            <div className="no-data-overlay" colSpan="10">
                              <h6>NO DATA <br />AVAILABLE</h6>
                              <span style={{top:'85%'}}>Data unavailable ! Please wait for systems to get updated with activity data</span>
                            </div>
                          </div>
                        )}
                    </div>
                  </div>
                </div>
              </div>
            )}
        </div>
      </div>
      }
    </div>
  );
};

export default VideoInterviewsReport;
